import React from "react";
import { Link, useLocation } from "react-router-dom";
import { FileText, Download, ArrowRight, ArrowLeft } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { getReglement } from "../lib/reglements";

export default function Reglement() {
  const slug = useLocation().pathname.replace(/^\/+|\/+$/g, "");
  const doc = getReglement(slug);

  useSEO({
    title: doc ? doc.seo_title : "Règlement — 1000 ARTS",
    description: doc ? doc.seo_desc : "Règlements de l'école 1000 ARTS.",
    canonical: `/${slug}`,
  });

  if (!doc) return null;

  const autre = slug === "reglement" ? "reglement-competition" : "reglement";
  const autreDoc = getReglement(autre);

  return (
    <div data-testid={`page-${doc.slug}`} className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[860px] mx-auto px-6 md:px-12 relative z-10">
        <div className="eyebrow mb-3">{doc.eyebrow}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
          {doc.title}
        </h1>
        <p className="mt-6 text-base md:text-lg text-[var(--muted)] leading-relaxed">{doc.intro}</p>

        {doc.pdf ? (
          <a
            href={doc.pdf}
            target="_blank"
            rel="noreferrer"
            data-testid={`${doc.slug}-pdf-link`}
            className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full font-semibold text-sm transition-transform hover:-translate-y-0.5"
            style={{ background: "var(--primary)", color: "#fff" }}
          >
            <Download size={16} /> {doc.pdf_label}
          </a>
        ) : null}

        {doc.version ? (
          <div
            data-testid={`${doc.slug}-version`}
            className="inline-flex items-center gap-2 mt-8 px-4 py-2 rounded-full text-xs font-semibold"
            style={{ background: "var(--primary-tint)", color: "var(--primary)" }}
          >
            <FileText size={14} /> {doc.version} · cette page fait foi
          </div>
        ) : null}

        {(doc.blocs || []).map((b) => (
          <Section key={b.title} title={b.title} testId={`bloc-${slugify(b.title)}`}>
            {b.text ? <P>{b.text}</P> : null}
            {b.items ? <Items items={b.items} /> : null}
          </Section>
        ))}

        {doc.sections.map((s) => (
          <Section key={s.n} title={`${s.n}. ${s.title}`} testId={`article-${s.n}`}>
            <Items items={s.items} />
          </Section>
        ))}

        {doc.clause ? (
          <div className="card-flat mt-12" style={{ background: "var(--primary-tint)", borderColor: "transparent" }}>
            <p className="text-sm leading-relaxed">{doc.clause}</p>
          </div>
        ) : null}

        <div className="mt-14 flex flex-wrap items-center gap-4">
          <Link
            to={`/${autre}`}
            data-testid="reglement-autre-link"
            className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: "var(--primary)" }}
          >
            <FileText size={15} /> {autreDoc.title} <ArrowRight size={14} />
          </Link>
          <Link
            to="/inscription"
            data-testid="reglement-inscription-link"
            className="inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--primary)]"
          >
            <ArrowLeft size={14} /> Retour aux inscriptions
          </Link>
        </div>
      </div>
    </div>
  );
}

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId} className="mt-12 scroll-mt-28">
      <h2
        className="font-display text-base md:text-lg tracking-tight pb-3 mb-4 border-b-2"
        style={{ borderColor: "var(--primary)" }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function P({ children }) {
  return <p className="text-[15px] leading-relaxed text-[var(--text)] mt-3">{children}</p>;
}

function Items({ items }) {
  return (
    <ul className="mt-3 space-y-2">
      {items.map((it, i) => (
        <li key={i} className="text-[15px] leading-relaxed text-[var(--text)] flex gap-3">
          <span className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full" style={{ background: "var(--primary)" }} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
